// Small DOM + formatting helpers shared by every panel. No framework: panels build HTML strings,
// `patch()` only touches the DOM when the string changed, and events are delegated via data-act attributes.
import { formatDate } from '../core/time.js';

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

// Tagged template that escapes interpolations unless they are wrapped with raw().
export function html(strings, ...vals) {
  let out = strings[0];
  for (let i = 0; i < vals.length; i++) out += flat(vals[i]) + strings[i + 1];
  return out;
}
const RAW = Symbol('raw');
export const raw = (s) => ({ [RAW]: String(s ?? '') });
function flat(v) {
  if (v == null || v === false) return '';
  if (Array.isArray(v)) return v.map(flat).join('');
  if (typeof v === 'object' && RAW in v) return v[RAW];
  return esc(v);
}

// Replace innerHTML only if the markup changed. A control you're using inside `el` (typing, a select you just
// changed) keeps its value, caret and focus while the rest of the panel updates around it — so quotes, totals and
// previews follow your input even with the game paused. Before the first fill there's nothing to morph: wait for blur.
export function patch(el, markup) {
  if (!el) return false;
  if (el.__html === markup) return false;
  const a = document.activeElement;
  const held = a && el.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'SELECT' || a.tagName === 'TEXTAREA') ? a : null;
  if (held && (el.__html === undefined || patch.rebuild)) return false;
  const keep = held && { v: held.value, s: held.selectionStart ?? null, e: held.selectionEnd ?? null, c: held.checked };
  const scroll = el.scrollTop;
  // render-perf: after the first fill, morph the live DOM toward the new markup (parsed in an inert <template>)
  // instead of rebuilding it: unchanged subtrees are skipped (native isEqualNode), changed text / attributes are
  // updated in place, so a 4 Hz table refresh restyles a few cells instead of thousands of nodes.
  if (el.__html === undefined || !el.firstChild || patch.rebuild) el.innerHTML = markup;
  else { TPL.innerHTML = markup; morphChildren(el, TPL.content); TPL.innerHTML = ''; syncControls(el); }
  el.__html = markup;
  el.scrollTop = scroll;
  if (held?.isConnected) { // the control in your hand stays as you left it
    if (held.type === 'checkbox' || held.type === 'radio') held.checked = keep.c; else if (held.value !== keep.v) held.value = keep.v;
    if (keep.s != null && document.activeElement === held) try { held.setSelectionRange(keep.s, keep.e); } catch { /* number inputs */ }
  }
  return true;
}
patch.rebuild = false; // debug escape hatch: true = old innerHTML behaviour

const TPL = typeof document !== 'undefined' ? document.createElement('template') : null;
function morphChildren(dst, src) {
  let d = dst.firstChild, s = src.firstChild;
  while (s) {
    const sn = s.nextSibling;
    if (!d) dst.appendChild(s);
    else if (d.nodeType !== s.nodeType || d.nodeName !== s.nodeName) { const dn = d.nextSibling; dst.replaceChild(s, d); d = dn; }
    else { morphNode(d, s); d = d.nextSibling; }
    s = sn;
  }
  while (d) { const dn = d.nextSibling; dst.removeChild(d); d = dn; }
}
function morphNode(d, s) {
  if (d.nodeType !== 1) { if (d.nodeValue !== s.nodeValue) d.nodeValue = s.nodeValue; return; }
  if (d.isEqualNode(s)) return;
  const canvas = d.nodeName === 'CANVAS'; // keep its bitmap (width/height are set by the drawing code)
  const da = d.attributes, sa = s.attributes;
  for (let i = da.length - 1; i >= 0; i--) { const n = da[i].name; if (!s.hasAttribute(n) && !(canvas && (n === 'width' || n === 'height'))) d.removeAttribute(n); }
  for (let i = 0; i < sa.length; i++) { const { name, value } = sa[i]; if (d.getAttribute(name) !== value) d.setAttribute(name, value); }
  if (!canvas) morphChildren(d, s);
}
// innerHTML semantics for form controls: they show what the markup says (the patch never runs while one is focused)
function syncControls(el) {
  for (const c of el.querySelectorAll('input, select')) {
    if (c.tagName === 'SELECT') { if (!c.multiple) { const o = c.querySelector('option[selected]') ?? c.options[0]; if (o && !o.selected) o.selected = true; } }
    else if (c.type === 'checkbox' || c.type === 'radio') { const v = c.hasAttribute('checked'); if (c.checked !== v) c.checked = v; }
    else if (c.type !== 'file' && c.hasAttribute('value')) { const v = c.getAttribute('value'); if (c.value !== v) c.value = v; }
  }
}

// Delegate clicks on [data-act] within root. handler(act, dataset, el, ev)
export function delegate(root, type, handler) {
  root.addEventListener(type, (ev) => {
    const el = ev.target.closest('[data-act]');
    if (!el || !root.contains(el) || el.disabled) return;
    if (type === 'click' && (el.tagName === 'INPUT' && el.type !== 'checkbox')) return;
    if (el.tagName === 'A') ev.preventDefault();
    handler(el.dataset.act, el.dataset, el, ev);
  });
}

// ---------- formatting ----------
// polish C6: cents only below $100 — "$2,127", not "$2,127.14" (pass cents:false to drop them everywhere).
export function money(n, { cents, sign } = {}) {
  if (n == null || !Number.isFinite(n)) return '—';
  const neg = n < 0;
  const a = Math.abs(n);
  let s;
  if (a >= 1e9) s = (a / 1e9).toFixed(2) + 'B';
  else if (a >= 1e6) s = (a / 1e6).toFixed(2) + 'M';
  else if (a < 100 && cents !== false) s = a.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  else s = Math.round(a).toLocaleString('en-US');
  return (neg ? '−' : sign ? '+' : '') + '$' + s;
}
export const price = (n) => money(n, { cents: true });
export function num(n, d = 0) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}
export const ft = (n) => (n == null ? '—' : num(Math.round(n)) + ' ft');
export const bbl = (n) => (n == null ? '—' : num(Math.round(n)) + ' bbl');
export const pct = (n, d = 0) => (n == null ? '—' : num(n * 100, d) + '%');
export const date = (day) => formatDate(day);
export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
export const cap = (s) => String(s ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
export const tokens = (n) => '◈ ' + num(n ?? 0);

export function bar(frac, cls = '', title = '') {
  const w = clamp((frac ?? 0) * 100, 0, 100).toFixed(1);
  return `<div class="bar ${cls}" title="${esc(title)}"><i style="width:${w}%"></i></div>`;
}
// Bar colour by level: high = good (green) unless invert.
export function levelBar(frac, invert = false, title = '') {
  const f = clamp(frac ?? 0, 0, 1);
  const good = invert ? 1 - f : f;
  return bar(f, good > 0.6 ? 'g' : good < 0.3 ? 'r' : '', title);
}

export const statusLabel = (s) => cap(String(s ?? '').toLowerCase());
